import React from "react";
import { ArrowLeft, Edit3, Loader2, Plus, Trash2 } from "lucide-react";

export const districtCourtTheme = {
  primary: "#f26422",
  primarySoft: "#fde8d8",
  primaryBorder: "#fcc09a",
  shell: "#F1F5FD",
  border: "#E8EDF5",
  muted: "#64748B",
};

export const BackButton = ({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-[#f26422]"
  >
    <ArrowLeft className="h-4 w-4" />
    {label}
  </button>
);

export const PrimaryButton = ({
  label,
  onClick,
  className = "",
}: {
  label: string;
  onClick: () => void;
  className?: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-[#f26422] px-4 text-sm font-semibold text-white transition hover:bg-[#d9571d] ${className}`}
  >
    <Plus className="h-4 w-4" />
    {label}
  </button>
);

export const CountBadge = ({ label }: { label: string }) => (
  <span className="rounded-full bg-[#fde8d8] px-3 py-1 text-xs font-semibold text-[#f26422]">
    {label}
  </span>
);

export const StatusBadge = ({ status }: { status?: string }) => {
  const isActive = (status || "active").toLowerCase() === "active";

  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${
        isActive ? "bg-green-100 text-green-600" : "bg-slate-100 text-slate-500"
      }`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${
          isActive ? "bg-green-600" : "bg-slate-400"
        }`}
      />
      {isActive ? "Active" : status || "Inactive"}
    </span>
  );
};

export const OrangeIconBox = ({
  children,
  small = false,
}: {
  children: React.ReactNode;
  small?: boolean;
}) => (
  <span
    className={`flex items-center justify-center rounded-xl bg-[#fde8d8] text-[#f26422] ${
      small ? "h-11 w-11" : "h-12 w-12"
    }`}
  >
    {children}
  </span>
);

export const RowActions = ({
  onEdit,
  onDelete,
}: {
  onEdit: () => void;
  onDelete: () => void;
}) => (
  <div className="flex items-center gap-2">
    <button
      type="button"
      title="Edit"
      onClick={onEdit}
      className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#E2E8F0] bg-white text-[#f26422] transition hover:border-[#fcc09a] hover:bg-[#fde8d8]"
    >
      <Edit3 className="h-4 w-4" />
    </button>
    <button
      type="button"
      title="Delete"
      onClick={onDelete}
      className="flex h-8 w-8 items-center justify-center rounded-lg border border-[#E2E8F0] bg-white text-red-500 transition hover:border-red-200 hover:bg-red-50"
    >
      <Trash2 className="h-4 w-4" />
    </button>
  </div>
);

export const ScreenTitle = ({
  title,
  count,
  subtitle,
  actionLabel,
  onAction,
}: {
  title: string;
  count?: string;
  subtitle: string;
  actionLabel?: string;
  onAction?: () => void;
}) => (
  <div className="mb-5 flex flex-col items-stretch gap-4 sm:flex-row sm:items-start sm:justify-between">
    <div className="min-w-0">
      <div className="mb-1 flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-bold text-slate-950">{title}</h1>
        {count && <CountBadge label={count} />}
      </div>
      <p className="text-sm text-slate-500">{subtitle}</p>
    </div>
    {actionLabel && onAction && (
      <PrimaryButton label={actionLabel} onClick={onAction} className="w-full sm:w-auto" />
    )}
  </div>
);

export const DetailHeader = ({
  icon,
  title,
  meta,
  badges = [],
  stats,
}: {
  icon: React.ReactNode;
  title: string;
  meta: React.ReactNode;
  badges?: React.ReactNode[];
  stats: Array<{ label: string; value: number | string; dark?: boolean }>;
}) => (
  <section className="mb-5 flex flex-col gap-5 rounded-xl border border-[#E8EDF5] bg-white px-6 py-5 lg:flex-row lg:items-center lg:justify-between">
    <div className="flex min-w-0 flex-col gap-4 sm:flex-row sm:items-center">
      <OrangeIconBox>{icon}</OrangeIconBox>
      <div className="min-w-0">
        <div className="mb-1 flex flex-wrap items-center gap-3">
          <h1 className="min-w-0 text-lg font-bold text-slate-950">{title}</h1>
          <StatusBadge status="Active" />
          {badges.map((badge, index) => (
            <React.Fragment key={index}>{badge}</React.Fragment>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
          {meta}
        </div>
      </div>
    </div>
    <div className="overflow-hidden rounded-xl border border-[#E8EDF5]">
      {stats.map((item, index) => (
        <span
          key={item.label}
          className={`inline-block px-6 py-3 text-center ${
            index > 0 ? "border-l border-[#E8EDF5]" : ""
          }`}
        >
          <span
            className={`block text-xl font-bold ${
              item.dark ? "text-slate-950" : "text-[#f26422]"
            }`}
          >
            {item.value}
          </span>
          <span className="block text-xs text-slate-400">{item.label}</span>
        </span>
      ))}
    </div>
  </section>
);

export const DataTable = ({
  headers,
  isLoading,
  emptyLabel,
  emptyAction,
  onEmptyAction,
  children,
  mobileCards,
}: {
  headers: string[];
  isLoading: boolean;
  emptyLabel: string;
  emptyAction: string;
  onEmptyAction: () => void;
  children: React.ReactNode;
  mobileCards?: React.ReactNode;
}) => (
  <>
    <div className="hidden overflow-x-auto rounded-xl border border-[#E8EDF5] bg-white md:block">
      <table className="w-full min-w-[920px]">
        <thead className="border-b border-[#E8EDF5] bg-slate-50 text-left text-xs uppercase tracking-widest text-slate-500">
          <tr>
            {headers.map((header) => (
              <th key={header} className="px-5 py-3">
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {isLoading ? (
            <tr>
              <td colSpan={headers.length} className="px-5 py-10 text-center">
                <Loader2 className="mx-auto h-6 w-6 animate-spin text-[#f26422]" />
              </td>
            </tr>
          ) : React.Children.count(children) ? (
            children
          ) : (
            <tr>
              <td colSpan={headers.length} className="px-5 py-12 text-center">
                <p className="mb-3 text-sm font-semibold text-slate-400">
                  {emptyLabel}
                </p>
                <button
                  type="button"
                  onClick={onEmptyAction}
                  className="rounded-lg bg-[#fde8d8] px-4 py-2 text-sm font-semibold text-[#f26422]"
                >
                  + {emptyAction}
                </button>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
    <div className="space-y-3 md:hidden">
      {isLoading ? (
        <div className="rounded-xl border border-[#E8EDF5] bg-white px-4 py-10 text-center">
          <Loader2 className="mx-auto h-6 w-6 animate-spin text-[#f26422]" />
        </div>
      ) : React.Children.count(children) ? (
        mobileCards || children
      ) : (
        <div className="rounded-xl border border-[#E8EDF5] bg-white px-4 py-10 text-center">
          <p className="mb-3 text-sm font-semibold text-slate-400">
            {emptyLabel}
          </p>
          <button
            type="button"
            onClick={onEmptyAction}
            className="rounded-lg bg-[#fde8d8] px-4 py-2 text-sm font-semibold text-[#f26422]"
          >
            + {emptyAction}
          </button>
        </div>
      )}
    </div>
  </>
);

export const rowClassName = "transition hover:bg-green-50";

export const MobileInfoRow = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => (
  <div className="flex items-start justify-between gap-4 border-t border-slate-100 pt-3 text-sm">
    <span className="text-slate-400">{label}</span>
    <span className="min-w-0 text-right font-medium text-slate-700">{value}</span>
  </div>
);
